import { Text, Flex, Icon, Stack } from "@chakra-ui/react";
import arrowLeft from "@/assets/svg/backIcon.svg?react";
import { useLocation, useNavigate } from "react-router-dom";
import { NAVIGATION_ROUTES } from "@/routes/constants";
import CustomButton from "@/components/common/Button";
import MailIcon from "@/assets/svg/mail.svg?react";
import { useSendResetPasswordEmailMutation } from "@/services/forgotPassword/forgotPassword";
import { useState, useEffect } from "react";
import OutlineButton from "@/components/common/UtilityButton";
import AuthWrapper from "@/layouts/AuthWrapper";

const ForgotPasswordLink = () => {
  const navigate = useNavigate();
  const { mutateAsync: sendResetEmail } = useSendResetPasswordEmailMutation();
  const location = useLocation();
  const email = location.state?.email || "rudeep.khatiwada@arclogi.com";

  const initialTimer = () => {
    const storedTime = localStorage.getItem("resendTimer");
    const currentTime = Date.now();
    if (storedTime) {
      const remainingTime = Math.ceil(
        (Number(storedTime) - currentTime) / 1000
      );
      return Math.max(0, remainingTime);
    }
    return 120;
  };

  const [timer, setTimer] = useState(initialTimer);
  const [isResendDisabled, setIsResendDisabled] = useState(timer > 0);

  useEffect(() => {
    let countdown: ReturnType<typeof setInterval>;

    if (timer > 0) {
      countdown = setInterval(() => {
        setTimer((prev) => {
          const updatedTime = prev - 1;
          if (updatedTime > 0) {
            localStorage.setItem(
              "resendTimer",
              (Date.now() + updatedTime * 1000).toString()
            );
          }
          return updatedTime;
        });
      }, 1000);
    } else {
      setIsResendDisabled(false);
      localStorage.removeItem("resendTimer");
    }
    return () => clearInterval(countdown);
  }, [timer]);

  const handleResend = async () => {
    setIsResendDisabled(true);
    setTimer(120);
    localStorage.setItem("resendTimer", (Date.now() + 120 * 1000).toString());
    await sendResetEmail(email);
  };

  const formatTimer = () => {
    const minutes = Math.floor(timer / 60);
    const seconds = timer % 60;
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  return (
    <AuthWrapper>
      <Flex
        w="full"
        maxW={"500px"}
        gap={8}
        borderRadius="md"
        flexDirection="column"
        justifyContent="center"
        bg="white"
      >
        <Icon
          as={MailIcon}
          fill="white"
          boxSize={"56px"}
          p={3}
          borderRadius={"md"}
          bg="primary"
          color={"brand.primary.DEFAULT"}
          border={"1px solid"}
          borderColor={"border.default"}
        />
        <Stack gap={1}>
          <Text
            color={"text.primary"}
            fontSize={{ base: "2xl", lg: "4xl" }}
            fontFamily={"primary"}
            fontWeight={"semibold"}
          >
            Check your email
          </Text>
          <Text fontSize="sm" color="text.secondary" fontFamily="primary">
            We sent a password reset link to{" "}
            <Text as="span" color="text.primary" fontWeight="bold">
              {email}
            </Text>
            .
          </Text>
        </Stack>

        <CustomButton
          width="100%"
          fontSize="md "
          fontWeight="semibold"
          fontFamily={"primary"}
          onClick={() => (window.location.href = `mailto:${email}`)}
        >
          Open Email App
        </CustomButton>

        <Text
          fontSize="sm"
          color="text.secondary"
          textAlign="center"
          fontFamily={"primary"}
        >
          Didn’t receive the email?{" "}
          {isResendDisabled ? (
            <Text as="span" color="gray.400" fontWeight="semibold">
              Resend in {formatTimer()}
            </Text>
          ) : (
            <Text
              as="span"
              color="primary"
              fontWeight="semibold"
              cursor="pointer"
              onClick={handleResend}
            >
              Resend
            </Text>
          )}
        </Text>
        <OutlineButton
          alignSelf={"center"}
          variant="noStyle"
          width="fit-content"
          fontSize="14px"
          fontWeight="semibold"
          fontFamily={"primary"}
          border={"none !important"}
          outline={"none"}
          icon={arrowLeft}
          text="Back to Sign in"
          color={"brand.primary.DEFAULT"}
          onClick={() => navigate(NAVIGATION_ROUTES.SIGNIN)}
          _hover={{ color: "brand.primary.700" }}
        ></OutlineButton>
      </Flex>
    </AuthWrapper>
  );
};

export default ForgotPasswordLink;
